import * as Yup from "yup";
import { Formik } from "formik";
import { Loader2Icon } from "lucide-react";
import { type TableColumn } from "react-data-table-component";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CustomDataTable, CustomInput, CustomCombobox } from "@/components";
import { useMainLayoutContext } from "@/context/MainLayoutContext";
import {
  allocateSubjectToTeacher,
  deleteSubjectAllocation,
} from "@/api/admin";
import { Button } from "@/components/ui/button";
import { showSuccessToast } from "@/lib/toast";
import { Teacher } from "@/types/user.types";
import { TrashIcon } from "@/assets/icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { getSubjectAllocations } from "@/api/teachers";
import { Allocation } from "@/types/allocations.types";
import useAuth from "@/hooks/useAuth";

type InstructorAllocationsProps = {
  currentInstructor: Teacher;
  onClose?: () => void;
};
const columns = (instructorId: string): TableColumn<Allocation>[] => ([
  { name: "S/N", selector: (_, index = 0) => index + 1, width: "90px" },
  {
    name: "Subject",
    cell: (row) => row.subject,
    width: "200px",
  },
  {
    name: "Class",
    cell: (row) => (
      <span className="capitalize">
        {row.class.trim()}
      </span>
    ),
  },
  {
    name: "Actions",
    cell: (row) => {
      return <DeleteAllocation row={row} instructorId={instructorId} />;
    },
  },
]);

const InstructorAllocations = ({ currentInstructor }: InstructorAllocationsProps) => {
  const { subjects, classes } = useMainLayoutContext();
  const { currentSessionId } = useAuth()
  const queryClient = useQueryClient();

  const { data, isLoading: instructorIsLoading, isFetching } = useQuery({
    queryKey: ["teacher-allocations", currentInstructor?.id, currentSessionId],
    queryFn: () => getSubjectAllocations({ teacher_id: currentInstructor?.id, session_id: currentSessionId }),
  });

  const initialValues = {
    teacherID: currentInstructor?.id,
    classID: "",
    subjectID: "",
  };
  const validationSchema = Yup.object().shape({
    teacherID: Yup.string().required("Please select a teacher"),
    classID: Yup.string().required("Please select a class"),
    subjectID: Yup.string().required("Please select a subject"),
  });

  const { mutate, isLoading: allocating } = useMutation({
    mutationFn: allocateSubjectToTeacher,
    onSuccess: () => {
      showSuccessToast(`Subject allocated successfully`,);
      queryClient.invalidateQueries({
        queryKey: ["teacher-allocations", currentInstructor?.id, currentSessionId],
        exact: true,
      });
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => mutate(values)}
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, setFieldValue }) => (
        <div className="flex h-full flex-grow flex-col gap-8.5">
          <form onSubmit={handleSubmit} className="grid gap-6 pt-6">
            <div className="grid gap-5 md:grid-cols-2">
              <CustomInput
                label="First Name"
                id="firstname"
                value={currentInstructor?.firstname || ""}
                disabled
              />
              <CustomInput
                label="Last Name"
                id="surname"
                value={currentInstructor?.surname || ""}
                disabled
              />
              <CustomCombobox
                label="Class"
                className="h-12"
                options={classes?.map((classItem) => ({
                  value: classItem.id,
                  label: classItem.class,
                })) || []}
                value={values.classID}
                name="classID"
                onChange={(val) => setFieldValue("classID", val)}
                placeholder="Select class"
              />
              <CustomCombobox
                options={subjects?.map((subject) => ({
                  value: subject.id,
                  label: subject.title,
                })) || []}
                className="h-12"
                value={values.subjectID}
                name="subjectID"
                onChange={(val) => setFieldValue("subjectID", val)}
                placeholder="Select subject"
                label="Subject"
              />
            </div>

            <Button
              type="submit"
              className="ml-auto h-8.5 min-w-[100px] max-w-[139px] rounded-lg"
              disabled={allocating}
            >
              Save Allocation
            </Button>
          </form>

          <CustomDataTable
            data={data?.data || []}
            columns={columns(currentInstructor.id)}
            emptyStateMessage="No allocations"
            progressPending={instructorIsLoading || isFetching}
          />
        </div>
      )}
    </Formik>
  );
};

function DeleteAllocation({ row, instructorId }: { row: Allocation, instructorId: string }) {
  const [confirming, setConfirming] = useState(false);
  const { currentSessionId } = useAuth()
  const queryClient = useQueryClient();

  const { mutate: deleteAllocation, isLoading: deletingAllocation } =
    useMutation({
      mutationFn: deleteSubjectAllocation,
      onSuccess: (data) => {
        showSuccessToast("Subject allocation removed");
        queryClient.invalidateQueries({
          queryKey: ["teacher-allocations", instructorId, currentSessionId],
        });
        setConfirming(false);
      },
    });

  return (
    <AlertDialog open={confirming}>
      <AlertDialogTrigger onClick={() => setConfirming(true)} asChild>
        <Button
          type="submit"
          disabled={deletingAllocation}
          variant="custom"
          className="space-x-2 text-destructive"
        >
          {deletingAllocation ? (
            "Deleting..."
          ) : (
            <>
              <TrashIcon /> <span>Delete</span>
            </>
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to delete <b>{row.subject}</b>{" "}
            from this teacher's allocations. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => setConfirming(false)}
            className="border-border text-foreground"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="min-w-16 bg-destructive text-destructive-foreground"
            onClick={() => deleteAllocation({ tsID: row?.id })}
            disabled={deletingAllocation}
          >
            {deletingAllocation ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default InstructorAllocations;
